import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Settings, Save, Building, FileText, Receipt, 
  AlertTriangle, Euro, Clock, Mail, MessageSquare, X, Package, Database, RefreshCw, Phone, Printer, Eye, Tag
} from 'lucide-react';
import { listPrinters, findLabelPrinter, findPosPrinter, generateDepositLabelHtml } from '@/utils/qzTrayService';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function SettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  
  // Company info for invoices (separate from internal settings)
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    legal_name: '',
    address: '',
    postal_code: '',
    city: '',
    phone: '',
    email: '',
    siret: '',
    tva_number: '',
    website: ''
  });
  
  // Company settings (internal)
  const [companySettings, setCompanySettings] = useState({
    name: 'PHONELAB',
    address: '5 Rue Paul Verlaine',
    postalCode: '69100',
    city: 'VILLEURBANNE',
    phone: '04 26 00 82 10',
    email: '',
    siret: '',
    tvaNumber: '',
    logo: '',
    iban: '',
    bic: ''
  });

  // Invoice settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    conditions: `Paiement à réception de facture.
En cas de retard de paiement, une pénalité de 3 fois le taux d'intérêt légal sera appliquée.
Pas d'escompte pour paiement anticipé.`,
    legalMentions: `TVA non applicable, art. 293 B du CGI`,
    footerText: '',
    paymentTermsDays: 30
  });

  // Deposit receipt settings
  const [receiptSettings, setReceiptSettings] = useState({
    // Conditions générales
    generalConditions: `En déposant votre appareil, vous acceptez les conditions suivantes :
- Le client reste responsable de la sauvegarde de ses données
- PHONELAB décline toute responsabilité en cas de perte de données
- Le devis est gratuit et sans engagement`,
    
    // Règles de gardiennage
    storageDays: 30,
    storageWarningDays: 20,
    storageFeeAmount: 10,
    storageFeePerWeek: 5,
    maxStorageDays: 90,
    
    // Texte destruction
    destructionWarning: `ATTENTION : Tout appareil non récupéré dans un délai de {storageDays} jours après notification de fin de réparation sera considéré comme abandonné et pourra être détruit ou recyclé sans indemnité.`,
    
    // Frais de gardiennage
    storageFeeText: `Au-delà de {storageDays} jours, des frais de gardiennage de {storageFeeAmount}€ seront appliqués, puis {storageFeePerWeek}€ par semaine supplémentaire.`,
    
    // Messages de notification
    creationSmsText: `{company}: Dépôt confirmé! Ticket N°{ticket}. Appareil: {device}. Nous vous contacterons dès que la réparation sera terminée. Merci de votre confiance!`,
    
    repairedSmsText: `{company}: Votre {device} (Ticket N°{ticket}) est PRÊT! Venez le récupérer. Rappel: retrait sous {storageDays} jours.`,
    
    reminderSmsText: `{company}: RAPPEL - Votre {device} (Ticket N°{ticket}) vous attend depuis {days} jours. Passé {storageDays} jours, des frais de gardiennage s'appliqueront. Merci de venir le récupérer.`,
    
    storageSmsText: `{company}: ATTENTION - Votre {device} (Ticket N°{ticket}) est en gardiennage. Frais: {fee}€. Pour prolonger ou confirmer le gardiennage, répondez OUI. Sans réponse sous 7 jours, l'appareil sera considéré abandonné.`,
    
    // Messages email
    creationEmailSubject: `Confirmation de dépôt - Ticket N°{ticket}`,
    repairedEmailSubject: `Votre appareil est prêt ! - Ticket N°{ticket}`,
    reminderEmailSubject: `Rappel : Venez récupérer votre appareil - Ticket N°{ticket}`,
    storageEmailSubject: `URGENT : Frais de gardiennage - Ticket N°{ticket}`,
    
    // Rappels automatiques
    reminderEnabled: true,
    reminderDay1: 7,
    reminderDay2: 3,
    reminderDay3: 1
  });

  // Order alerts settings
  const [orderSettings, setOrderSettings] = useState({
    // Messages SMS
    pendingSmsText: `{company}: Votre commande de {product} a été enregistrée. Nous vous préviendrons dès qu'elle sera passée.`,
    orderedSmsText: `{company}: Bonne nouvelle! Votre {product} a été commandé. Délai estimé: quelques jours.`,
    receivedSmsText: `{company}: Votre {product} est ARRIVÉ! Venez le récupérer dès que possible.`,
    deliveredSmsText: `{company}: Merci d'avoir récupéré votre {product}! À bientôt.`,
    
    // Messages email
    pendingEmailSubject: `Commande enregistrée - {product}`,
    orderedEmailSubject: `Commande passée - {product}`,
    receivedEmailSubject: `Article disponible ! - {product}`,
    deliveredEmailSubject: `Merci pour votre confiance`,
    
    // Notifications automatiques
    autoNotifyOnStatusChange: true,
    defaultNotifyChannels: ['sms']
  });

  // Printer settings
  const [printerSettings, setPrinterSettings] = useState(() => {
    const saved = localStorage.getItem('printerSettings');
    return saved ? JSON.parse(saved) : {
      posPrinterName: '',
      labelPrinterName: '',
      ticketWidth: 58,
      labelWidth: 50,
      labelHeight: 30
    };
  });
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  // Label settings (paramètres de l'étiquette)
  const [labelSettings, setLabelSettings] = useState(() => {
    const saved = localStorage.getItem('labelSettings');
    return saved ? JSON.parse(saved) : {
      autoSize: true,
      baseFontSize: 28,
      showTicket: true,
      showDate: true,
      showPhone: true,
      showProblem: true,
      showPrice: true,
      showPin: true,
      showClient: true,
      ticketSize: 20,
      dateSize: 12,
      phoneSize: 28,
      problemSize: 16,
      priceSize: 24,
      pinSize: 14,
      clientSize: 16
    };
  });

  // Exemple de bon pour l'aperçu
  const previewReceipt = {
    ticketNumber: 'PL260320-001',
    clientPhone: '0612345678',
    clientName: 'JEAN DUPONT',
    problem: 'Écran cassé + batterie',
    estimatedPrice: 89,
    pinCode: '1234',
    createdAt: new Date().toISOString()
  };

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/api/company-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCompanyInfo({
          name: response.data.name || '',
          legal_name: response.data.legal_name || '',
          address: response.data.address || '',
          postal_code: response.data.postal_code || '',
          city: response.data.city || '',
          phone: response.data.phone || '',
          email: response.data.email || '',
          siret: response.data.siret || '',
          tva_number: response.data.tva_number || '',
          website: response.data.website || ''
        });
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.company) {
        setCompanySettings(prev => ({ ...prev, ...response.data.company }));
      }
      if (response.data.invoice) {
        setInvoiceSettings(prev => ({ ...prev, ...response.data.invoice }));
      }
      if (response.data.receipt) {
        setReceiptSettings(prev => ({ ...prev, ...response.data.receipt }));
      }
      if (response.data.orders) {
        setOrderSettings(prev => ({ ...prev, ...response.data.orders }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCompanyInfo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/company-info`, companyInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Informations entreprise enregistrées');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Sauvegarder les paramètres internes
      await axios.post(`${API}/api/settings`, {
        company: companySettings,
        invoice: invoiceSettings,
        receipt: receiptSettings,
        orders: orderSettings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sauvegarder aussi les infos de l'entreprise pour les factures
      await axios.put(`${API}/api/company-info`, companyInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Paramètres enregistrés avec succès');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Vérifier si une migration est nécessaire
  const checkMigrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/migration-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMigrationStatus(response.data);
    } catch (error) {
      console.error('Error checking migration status:', error);
    }
  };

  // Effectuer la migration
  const performMigration = async () => {
    if (!window.confirm('Cette action va associer toutes les données (factures, clients, etc.) à votre compte actuel. Continuer ?')) {
      return;
    }
    
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/api/settings/migrate-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setMigrationStatus({ needs_migration: false, total_orphan_documents: 0 });
      }
    } catch (error) {
      toast.error('Erreur lors de la migration');
      console.error(error);
    } finally {
      setMigrating(false);
    }
  };

  // Vérifier la migration au chargement
  useEffect(() => {
    checkMigrationStatus();
  }, []);

  // Charger la liste des imprimantes
  const loadPrinters = async () => {
    setLoadingPrinters(true);
    try {
      const printers = await listPrinters();
      setAvailablePrinters(printers);
      if (printers.length === 0) {
        toast.warning('Aucune imprimante détectée. Vérifiez que QZ Tray est lancé.');
      }
    } catch (error) {
      console.error('Error loading printers:', error);
      toast.error('Erreur lors du chargement des imprimantes');
    } finally {
      setLoadingPrinters(false);
    }
  };

  // Auto-détecter les imprimantes
  const autoDetectPrinters = async () => {
    setLoadingPrinters(true);
    try {
      const printers = await listPrinters();
      setAvailablePrinters(printers);
      
      // Auto-détecter l'imprimante POS
      const posPrinter = await findPosPrinter();
      // Auto-détecter l'imprimante d'étiquettes
      const labelPrinter = await findLabelPrinter();
      
      const newSettings = { ...printerSettings };
      if (posPrinter) newSettings.posPrinterName = posPrinter;
      if (labelPrinter) newSettings.labelPrinterName = labelPrinter;
      
      setPrinterSettings(newSettings);
      
      if (posPrinter || labelPrinter) {
        toast.success(`Détection automatique: ${posPrinter ? 'POS trouvée' : ''} ${labelPrinter ? 'Étiquettes trouvée' : ''}`);
      } else {
        toast.warning('Aucune imprimante spécialisée détectée automatiquement');
      }
    } catch (error) {
      console.error('Error auto-detecting printers:', error);
      toast.error('Erreur lors de la détection automatique');
    } finally {
      setLoadingPrinters(false);
    }
  };

  // Sauvegarder les paramètres d'imprimantes
  const savePrinterSettings = () => {
    localStorage.setItem('printerSettings', JSON.stringify(printerSettings));
    // Aussi sauvegarder pour compatibilité avec les anciennes fonctions
    localStorage.setItem('labelPrinterName', printerSettings.labelPrinterName);
    localStorage.setItem('printerConfig', JSON.stringify({
      ...printerSettings,
      printerName: printerSettings.posPrinterName,
      ticketWidth: printerSettings.ticketWidth
    }));
    toast.success('Paramètres d\'imprimantes enregistrés');
  };

  // Sauvegarder les paramètres d'étiquette
  const saveLabelSettings = () => {
    localStorage.setItem('labelSettings', JSON.stringify(labelSettings));
    toast.success('Paramètres d\'étiquette enregistrés');
  };

  // Générer l'aperçu HTML de l'étiquette
  const getLabelPreviewHtml = () => {
    return generateDepositLabelHtml(previewReceipt, labelSettings);
  };

  const tabs = [
    { id: 'company', label: 'Entreprise', icon: Building },
    { id: 'invoice', label: 'Factures', icon: FileText },
    { id: 'receipt', label: 'Bons d\'entrée', icon: Receipt },
    { id: 'orders', label: 'Commandes', icon: Package },
    { id: 'printers', label: 'Imprimantes', icon: Printer },
    { id: 'labels', label: 'Étiquettes', icon: Tag }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-700 h-14 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <h1 className="font-heading text-xl font-bold text-white">GESTION GLOBALE - Paramètres</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <Home className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-3.5rem)] bg-gray-900/50 border-r border-gray-700 p-4">
          <nav className="space-y-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6">
          {/* Company Settings */}
          {activeTab === 'company' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Building className="w-6 h-6 text-blue-400" />
                Informations Entreprise (affichées sur les factures)
              </h2>
              
              <div className="bg-gray-800/50 rounded-xl border border-blue-500/30 p-6 space-y-6">
                <div className="flex items-center gap-2 text-blue-400 text-sm mb-4">
                  <FileText className="w-4 h-4" />
                  Ces informations apparaîtront sur vos factures et devis
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Nom commercial *</label>
                    <Input
                      value={companyInfo.name}
                      onChange={(e) => setCompanyInfo({...companyInfo, name: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="MPHONE"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Raison sociale</label>
                    <Input
                      value={companyInfo.legal_name}
                      onChange={(e) => setCompanyInfo({...companyInfo, legal_name: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="MPHONE CENTRE DE REPARATION MULTIMEDIA"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">Adresse</label>
                  <Input
                    value={companyInfo.address}
                    onChange={(e) => setCompanyInfo({...companyInfo, address: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="5 RUE PAUL VERLAINE"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Code postal</label>
                    <Input
                      value={companyInfo.postal_code}
                      onChange={(e) => setCompanyInfo({...companyInfo, postal_code: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="69100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Ville</label>
                    <Input
                      value={companyInfo.city}
                      onChange={(e) => setCompanyInfo({...companyInfo, city: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="VILLEURBANNE"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-white/70 mb-2 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-400" />
                      Téléphone (affiché sur les factures) *
                    </label>
                    <Input
                      value={companyInfo.phone}
                      onChange={(e) => setCompanyInfo({...companyInfo, phone: e.target.value})}
                      className="bg-white/10 border-green-500/30 text-white"
                      placeholder="04 26 00 82 10"
                    />
                    <p className="text-xs text-green-400 mt-1">Ce numéro sera affiché sur vos factures et devis</p>
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Email</label>
                    <Input
                      type="email"
                      value={companyInfo.email}
                      onChange={(e) => setCompanyInfo({...companyInfo, email: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="contact@phonelab69.fr"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">SIRET</label>
                    <Input
                      value={companyInfo.siret}
                      onChange={(e) => setCompanyInfo({...companyInfo, siret: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="852 793 801 00014"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">N° TVA Intracommunautaire</label>
                    <Input
                      value={companyInfo.tva_number}
                      onChange={(e) => setCompanyInfo({...companyInfo, tva_number: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="FR13852793801"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">Site web</label>
                  <Input
                    value={companyInfo.website}
                    onChange={(e) => setCompanyInfo({...companyInfo, website: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="www.phonelab69.fr"
                  />
                </div>

                <div className="pt-4 border-t border-white/10">
                  <Button
                    onClick={saveCompanyInfo}
                    disabled={saving}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Enregistrer les informations
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Paramètres internes (bons d'entrée, tickets) */}
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6 space-y-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-purple-400" />
                  Paramètres internes (tickets, bons d'entrée)
                </h3>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Nom sur les tickets</label>
                    <Input
                      value={companySettings.name}
                      onChange={(e) => setCompanySettings({...companySettings, name: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Téléphone sur les tickets</label>
                    <Input
                      value={companySettings.phone}
                      onChange={(e) => setCompanySettings({...companySettings, phone: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h4 className="text-md font-semibold text-white mb-4">Coordonnées bancaires</h4>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-white/70 mb-2">IBAN</label>
                      <Input
                        value={companySettings.iban}
                        onChange={(e) => setCompanySettings({...companySettings, iban: e.target.value})}
                        placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                        className="bg-white/10 border-white/20 text-white font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/70 mb-2">BIC</label>
                      <Input
                        value={companySettings.bic}
                        onChange={(e) => setCompanySettings({...companySettings, bic: e.target.value})}
                        className="bg-white/10 border-white/20 text-white font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Migration des données */}
              <div className="bg-gray-800/50 rounded-xl border border-amber-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Migration des données
                </h3>
                <p className="text-sm text-white/70">
                  Si vos factures ou clients importés n'apparaissent pas correctement, utilisez cette fonction pour les associer à votre compte.
                </p>
                
                {migrationStatus && migrationStatus.needs_migration ? (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                    <p className="text-amber-200 mb-3">
                      <strong>{migrationStatus.total_orphan_documents}</strong> documents à migrer :
                    </p>
                    <ul className="text-sm text-white/60 mb-4 space-y-1">
                      {Object.entries(migrationStatus.details || {}).map(([key, count]) => (
                        count > 0 && <li key={key}>• {key}: {count}</li>
                      ))}
                    </ul>
                    <Button
                      onClick={performMigration}
                      disabled={migrating}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      {migrating ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Migration en cours...
                        </>
                      ) : (
                        <>
                          <Database className="w-4 h-4 mr-2" />
                          Migrer mes données
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                    <p className="text-emerald-200">
                      ✓ Toutes vos données sont correctement associées à votre compte.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Invoice Settings */}
          {activeTab === 'invoice' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FileText className="w-6 h-6 text-green-400" />
                Paramètres Factures
              </h2>
              
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6 space-y-6">
                <div>
                  <label className="block text-sm text-white/70 mb-2">Délai de paiement (jours)</label>
                  <Input
                    type="number"
                    value={invoiceSettings.paymentTermsDays}
                    onChange={(e) => setInvoiceSettings({...invoiceSettings, paymentTermsDays: parseInt(e.target.value)})}
                    className="bg-white/10 border-white/20 text-white w-32"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">Conditions de paiement</label>
                  <textarea
                    value={invoiceSettings.conditions}
                    onChange={(e) => setInvoiceSettings({...invoiceSettings, conditions: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">Mentions légales</label>
                  <textarea
                    value={invoiceSettings.legalMentions}
                    onChange={(e) => setInvoiceSettings({...invoiceSettings, legalMentions: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">Texte de pied de page</label>
                  <Input
                    value={invoiceSettings.footerText}
                    onChange={(e) => setInvoiceSettings({...invoiceSettings, footerText: e.target.value})}
                    placeholder="Ex: Merci pour votre confiance !"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Receipt Settings */}
          {activeTab === 'receipt' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Receipt className="w-6 h-6 text-amber-400" />
                Paramètres Bons d'entrée
              </h2>
              
              {/* Conditions générales */}
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Conditions générales
                </h3>
                <textarea
                  value={receiptSettings.generalConditions}
                  onChange={(e) => setReceiptSettings({...receiptSettings, generalConditions: e.target.value})}
                  rows={5}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                />
              </div>

              {/* Règles de gardiennage */}
              <div className="bg-gray-800/50 rounded-xl border border-amber-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Règles de gardiennage et destruction
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Délai de retrait (jours)</label>
                    <Input
                      type="number"
                      value={receiptSettings.storageDays}
                      onChange={(e) => setReceiptSettings({...receiptSettings, storageDays: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Rappel avant (jours)</label>
                    <Input
                      type="number"
                      value={receiptSettings.storageWarningDays}
                      onChange={(e) => setReceiptSettings({...receiptSettings, storageWarningDays: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Délai max (jours)</label>
                    <Input
                      type="number"
                      value={receiptSettings.maxStorageDays}
                      onChange={(e) => setReceiptSettings({...receiptSettings, maxStorageDays: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2 flex items-center gap-1">
                      <Euro className="w-4 h-4" />
                      Frais de gardiennage initial (€)
                    </label>
                    <Input
                      type="number"
                      value={receiptSettings.storageFeeAmount}
                      onChange={(e) => setReceiptSettings({...receiptSettings, storageFeeAmount: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2 flex items-center gap-1">
                      <Euro className="w-4 h-4" />
                      Frais par semaine supplémentaire (€)
                    </label>
                    <Input
                      type="number"
                      value={receiptSettings.storageFeePerWeek}
                      onChange={(e) => setReceiptSettings({...receiptSettings, storageFeePerWeek: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">Message de destruction</label>
                  <textarea
                    value={receiptSettings.destructionWarning}
                    onChange={(e) => setReceiptSettings({...receiptSettings, destructionWarning: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/10 border border-amber-500/30 rounded-lg text-amber-200 resize-none"
                    placeholder="Utilisez {storageDays} pour le délai"
                  />
                  <p className="text-xs text-white/50 mt-1">Variables: {'{storageDays}'}, {'{storageFeeAmount}'}, {'{storageFeePerWeek}'}</p>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">Message frais de gardiennage</label>
                  <textarea
                    value={receiptSettings.storageFeeText}
                    onChange={(e) => setReceiptSettings({...receiptSettings, storageFeeText: e.target.value})}
                    rows={2}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                  />
                </div>
              </div>

              {/* Messages SMS */}
              <div className="bg-gray-800/50 rounded-xl border border-green-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-green-400 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Messages SMS
                </h3>
                <p className="text-sm text-white/50">Variables disponibles: {'{company}'}, {'{ticket}'}, {'{device}'}, {'{days}'}, {'{storageDays}'}, {'{fee}'}</p>
                
                <div>
                  <label className="block text-sm text-white/70 mb-2">SMS de confirmation (création)</label>
                  <textarea
                    value={receiptSettings.creationSmsText}
                    onChange={(e) => setReceiptSettings({...receiptSettings, creationSmsText: e.target.value})}
                    rows={2}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">SMS appareil prêt</label>
                  <textarea
                    value={receiptSettings.repairedSmsText}
                    onChange={(e) => setReceiptSettings({...receiptSettings, repairedSmsText: e.target.value})}
                    rows={2}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">SMS de rappel</label>
                  <textarea
                    value={receiptSettings.reminderSmsText}
                    onChange={(e) => setReceiptSettings({...receiptSettings, reminderSmsText: e.target.value})}
                    rows={2}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">SMS frais de gardiennage</label>
                  <textarea
                    value={receiptSettings.storageSmsText}
                    onChange={(e) => setReceiptSettings({...receiptSettings, storageSmsText: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/10 border border-amber-500/30 rounded-lg text-white resize-none"
                  />
                </div>
              </div>

              {/* Messages Email */}
              <div className="bg-gray-800/50 rounded-xl border border-blue-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-blue-400 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Sujets des emails
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Email de création</label>
                    <Input
                      value={receiptSettings.creationEmailSubject}
                      onChange={(e) => setReceiptSettings({...receiptSettings, creationEmailSubject: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Email appareil prêt</label>
                    <Input
                      value={receiptSettings.repairedEmailSubject}
                      onChange={(e) => setReceiptSettings({...receiptSettings, repairedEmailSubject: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Email de rappel</label>
                    <Input
                      value={receiptSettings.reminderEmailSubject}
                      onChange={(e) => setReceiptSettings({...receiptSettings, reminderEmailSubject: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Email frais gardiennage</label>
                    <Input
                      value={receiptSettings.storageEmailSubject}
                      onChange={(e) => setReceiptSettings({...receiptSettings, storageEmailSubject: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Rappels automatiques */}
              <div className="bg-gray-800/50 rounded-xl border border-purple-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Rappels automatiques
                </h3>
                <p className="text-sm text-white/50">
                  Les rappels sont envoyés automatiquement tous les jours à 9h pour les appareils réparés mais non récupérés.
                </p>
                
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={receiptSettings.reminderEnabled !== false}
                      onChange={(e) => setReceiptSettings({...receiptSettings, reminderEnabled: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                  </label>
                  <span className="text-white">Activer les rappels automatiques</span>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">1er rappel (J-)</label>
                    <Input
                      type="number"
                      value={receiptSettings.reminderDay1 || 7}
                      onChange={(e) => setReceiptSettings({...receiptSettings, reminderDay1: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="7"
                    />
                    <p className="text-xs text-white/40 mt-1">jours avant deadline</p>
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">2ème rappel (J-)</label>
                    <Input
                      type="number"
                      value={receiptSettings.reminderDay2 || 3}
                      onChange={(e) => setReceiptSettings({...receiptSettings, reminderDay2: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="3"
                    />
                    <p className="text-xs text-white/40 mt-1">jours avant deadline</p>
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">3ème rappel (J-)</label>
                    <Input
                      type="number"
                      value={receiptSettings.reminderDay3 || 1}
                      onChange={(e) => setReceiptSettings({...receiptSettings, reminderDay3: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="1"
                    />
                    <p className="text-xs text-white/40 mt-1">jours avant deadline</p>
                  </div>
                </div>

                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mt-4">
                  <p className="text-sm text-purple-200">
                    <strong>Fonctionnement :</strong><br/>
                    • Un rappel est envoyé à J-7, J-3 et J-1 avant la deadline<br/>
                    • Le jour J, un message "DERNIER JOUR" est envoyé<br/>
                    • Après dépassement, un rappel hebdomadaire avec les frais accumulés est envoyé
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Orders Settings Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              {/* Messages SMS */}
              <div className="bg-gray-800/50 rounded-xl border border-green-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-green-400 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Messages SMS par statut
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">À commander</label>
                    <textarea
                      value={orderSettings.pendingSmsText}
                      onChange={(e) => setOrderSettings({...orderSettings, pendingSmsText: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Commandé</label>
                    <textarea
                      value={orderSettings.orderedSmsText}
                      onChange={(e) => setOrderSettings({...orderSettings, orderedSmsText: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Reçu (article disponible)</label>
                    <textarea
                      value={orderSettings.receivedSmsText}
                      onChange={(e) => setOrderSettings({...orderSettings, receivedSmsText: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Livré</label>
                    <textarea
                      value={orderSettings.deliveredSmsText}
                      onChange={(e) => setOrderSettings({...orderSettings, deliveredSmsText: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white resize-none"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-white/40">
                  Variables disponibles: {'{company}'}, {'{product}'}, {'{client}'}, {'{price}'}
                </p>
              </div>

              {/* Messages Email */}
              <div className="bg-gray-800/50 rounded-xl border border-blue-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-blue-400 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Sujets des emails
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">À commander</label>
                    <Input
                      value={orderSettings.pendingEmailSubject}
                      onChange={(e) => setOrderSettings({...orderSettings, pendingEmailSubject: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Commandé</label>
                    <Input
                      value={orderSettings.orderedEmailSubject}
                      onChange={(e) => setOrderSettings({...orderSettings, orderedEmailSubject: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Article disponible</label>
                    <Input
                      value={orderSettings.receivedEmailSubject}
                      onChange={(e) => setOrderSettings({...orderSettings, receivedEmailSubject: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Livré</label>
                    <Input
                      value={orderSettings.deliveredEmailSubject}
                      onChange={(e) => setOrderSettings({...orderSettings, deliveredEmailSubject: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Options de notification */}
              <div className="bg-gray-800/50 rounded-xl border border-amber-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Options de notification
                </h3>
                
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={orderSettings.autoNotifyOnStatusChange}
                      onChange={(e) => setOrderSettings({...orderSettings, autoNotifyOnStatusChange: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                  <span className="text-white">Notification automatique au changement de statut</span>
                </div>

                <div className="mt-4">
                  <label className="block text-sm text-white/70 mb-2">Canaux par défaut</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={orderSettings.defaultNotifyChannels?.includes('sms')}
                        onChange={(e) => {
                          const channels = orderSettings.defaultNotifyChannels || [];
                          if (e.target.checked) {
                            setOrderSettings({...orderSettings, defaultNotifyChannels: [...channels, 'sms']});
                          } else {
                            setOrderSettings({...orderSettings, defaultNotifyChannels: channels.filter(c => c !== 'sms')});
                          }
                        }}
                        className="w-4 h-4 rounded border-white/20 bg-white/10 text-amber-500"
                      />
                      <span className="text-sm text-white/70">SMS</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={orderSettings.defaultNotifyChannels?.includes('email')}
                        onChange={(e) => {
                          const channels = orderSettings.defaultNotifyChannels || [];
                          if (e.target.checked) {
                            setOrderSettings({...orderSettings, defaultNotifyChannels: [...channels, 'email']});
                          } else {
                            setOrderSettings({...orderSettings, defaultNotifyChannels: channels.filter(c => c !== 'email')});
                          }
                        }}
                        className="w-4 h-4 rounded border-white/20 bg-white/10 text-amber-500"
                      />
                      <span className="text-sm text-white/70">Email</span>
                    </label>
                  </div>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
                  <p className="text-sm text-amber-200">
                    <strong>Workflow des commandes :</strong><br/>
                    À commander → Commandé → Reçu → Livré (archivé)<br/>
                    Une notification peut être envoyée manuellement à chaque étape.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Onglet Imprimantes */}
          {activeTab === 'printers' && (
            <div className="space-y-6">
              {/* En-tête avec boutons */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Printer className="w-6 h-6 text-purple-400" />
                  Configuration des imprimantes
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={loadPrinters}
                    disabled={loadingPrinters}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingPrinters ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                  <Button
                    onClick={autoDetectPrinters}
                    disabled={loadingPrinters}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Détection auto
                  </Button>
                </div>
              </div>

              {/* Info QZ Tray */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                <p className="text-sm text-purple-200">
                  <strong>QZ Tray requis :</strong> L'impression passe par QZ Tray. Téléchargez-le sur{' '}
                  <a href="https://qz.io" target="_blank" rel="noopener noreferrer" className="text-purple-400 underline">qz.io</a>
                  {' '}et lancez-le avant d'imprimer.
                </p>
              </div>

              {/* Imprimante POS (Tickets) */}
              <div className="bg-gray-800/50 rounded-xl border border-green-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-green-400 flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Imprimante POS (Tickets & Bons)
                </h3>
                <p className="text-sm text-white/50">
                  Imprimante thermique pour les tickets de caisse et bons d'entrée
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Imprimante (liste ou saisie manuelle)</label>
                    <div className="space-y-2">
                      <select
                        value={availablePrinters.includes(printerSettings.posPrinterName) ? printerSettings.posPrinterName : ''}
                        onChange={(e) => setPrinterSettings({...printerSettings, posPrinterName: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white"
                      >
                        <option value="">-- Sélectionner dans la liste --</option>
                        {availablePrinters.map((printer, idx) => (
                          <option key={idx} value={printer}>{printer}</option>
                        ))}
                      </select>
                      <Input
                        value={printerSettings.posPrinterName}
                        onChange={(e) => setPrinterSettings({...printerSettings, posPrinterName: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Ou saisir le nom exact (ex: POS-80)"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Largeur du ticket (mm)</label>
                    <Input
                      type="number"
                      value={printerSettings.ticketWidth}
                      onChange={(e) => setPrinterSettings({...printerSettings, ticketWidth: parseInt(e.target.value) || 58})}
                      className="bg-white/10 border-white/20 text-white"
                      min="40"
                      max="80"
                    />
                  </div>
                </div>
                
                {printerSettings.posPrinterName && (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Imprimante configurée : {printerSettings.posPrinterName}
                  </div>
                )}
              </div>

              {/* Imprimante Étiquettes */}
              <div className="bg-gray-800/50 rounded-xl border border-amber-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Imprimante Étiquettes (Clabel CT221B ou autre)
                </h3>
                <p className="text-sm text-white/50">
                  Imprimante d'étiquettes autocollantes pour identifier les appareils
                </p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Imprimante (liste ou saisie manuelle)</label>
                    <div className="space-y-2">
                      <select
                        value={availablePrinters.includes(printerSettings.labelPrinterName) ? printerSettings.labelPrinterName : ''}
                        onChange={(e) => setPrinterSettings({...printerSettings, labelPrinterName: e.target.value})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white"
                      >
                        <option value="">-- Sélectionner dans la liste --</option>
                        {availablePrinters.map((printer, idx) => (
                          <option key={idx} value={printer}>{printer}</option>
                        ))}
                      </select>
                      <Input
                        value={printerSettings.labelPrinterName}
                        onChange={(e) => setPrinterSettings({...printerSettings, labelPrinterName: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Ou saisir: Clabel-CT221B"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Largeur étiquette (mm)</label>
                    <Input
                      type="number"
                      value={printerSettings.labelWidth}
                      onChange={(e) => setPrinterSettings({...printerSettings, labelWidth: parseInt(e.target.value) || 50})}
                      className="bg-white/10 border-white/20 text-white"
                      min="20"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-2">Hauteur étiquette (mm)</label>
                    <Input
                      type="number"
                      value={printerSettings.labelHeight}
                      onChange={(e) => setPrinterSettings({...printerSettings, labelHeight: parseInt(e.target.value) || 30})}
                      className="bg-white/10 border-white/20 text-white"
                      min="10"
                      max="100"
                    />
                  </div>
                </div>
                
                {printerSettings.labelPrinterName && (
                  <div className="flex items-center gap-2 text-amber-400 text-sm">
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                    Imprimante configurée : {printerSettings.labelPrinterName}
                  </div>
                )}
              </div>

              {/* Configuration du contenu de l'étiquette */}
              <div className="bg-gray-800/50 rounded-xl border border-purple-500/30 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Contenu de l'étiquette
                </h3>
                
                {/* Aperçu par défaut */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <p className="text-sm text-green-400 font-medium mb-2">✓ Configuration par défaut (optimisée pour 50x30mm)</p>
                  <div className="bg-white rounded p-2 text-black text-xs font-mono" style={{maxWidth: '180px'}}>
                    <div className="font-bold text-xs">NOM CLIENT</div>
                    <div className="flex justify-between"><span className="font-bold">#PL260319-134</span><span>19/03/26</span></div>
                    <div className="font-bold">0744228415</div>
                    <div className="text-xs truncate">REMPLACEMENT ECRAN + FORMAT..</div>
                    <div className="border-t border-dashed border-gray-400 my-1"></div>
                    <div className="flex justify-between items-center">
                      <span className="font-bold">59€</span>
                      <span className="bg-black text-white px-1 text-xs">1234</span>
                    </div>
                  </div>
                  <p className="text-xs text-white/50 mt-2">
                    ⚠️ Assurez-vous que le driver Windows est configuré en 50x30mm
                  </p>
                </div>

                {/* Toggle mode avancé */}
                <div className="flex items-center gap-3 pt-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={printerSettings.labelAdvancedMode === true}
                      onChange={(e) => setPrinterSettings({...printerSettings, labelAdvancedMode: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                  </label>
                  <span className="text-white">Activer le mode avancé (personnalisation manuelle)</span>
                </div>

                {/* Configuration avancée - visible seulement si activée */}
                {printerSettings.labelAdvancedMode && (
                  <div className="border-t border-purple-500/30 pt-4 mt-4 space-y-4">
                    <p className="text-sm text-amber-400">⚠️ Mode avancé activé - les paramètres ci-dessous remplacent la configuration par défaut</p>
                    
                    {/* Taille de police principale */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm text-white/70 mb-2">Taille police (pt)</label>
                        <Input
                          type="number"
                          value={printerSettings.labelFontSize || 8}
                          onChange={(e) => setPrinterSettings({...printerSettings, labelFontSize: parseInt(e.target.value) || 8})}
                          className="bg-white/10 border-white/20 text-white"
                          min="5"
                          max="14"
                        />
                        <p className="text-xs text-white/40 mt-1">Défaut: 8pt</p>
                      </div>
                      <div>
                        <label className="block text-sm text-white/70 mb-2">Max car. panne</label>
                        <Input
                          type="number"
                          value={printerSettings.labelProblemMaxChars || 30}
                          onChange={(e) => setPrinterSettings({...printerSettings, labelProblemMaxChars: parseInt(e.target.value) || 30})}
                          className="bg-white/10 border-white/20 text-white"
                          min="10"
                          max="50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-white/70 mb-2">Max car. nom</label>
                        <Input
                          type="number"
                          value={printerSettings.labelClientMaxChars || 15}
                          onChange={(e) => setPrinterSettings({...printerSettings, labelClientMaxChars: parseInt(e.target.value) || 15})}
                          className="bg-white/10 border-white/20 text-white"
                          min="5"
                          max="25"
                        />
                      </div>
                    </div>

                    {/* Éléments à afficher */}
                    <div>
                      <label className="block text-sm text-white/70 mb-3">Éléments à afficher</label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={printerSettings.labelShowTicket !== false}
                            onChange={(e) => setPrinterSettings({...printerSettings, labelShowTicket: e.target.checked})}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white/70">N° Ticket</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={printerSettings.labelShowDate !== false}
                            onChange={(e) => setPrinterSettings({...printerSettings, labelShowDate: e.target.checked})}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white/70">Date</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={printerSettings.labelShowPhone !== false}
                            onChange={(e) => setPrinterSettings({...printerSettings, labelShowPhone: e.target.checked})}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white/70">Téléphone</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={printerSettings.labelShowProblem !== false}
                            onChange={(e) => setPrinterSettings({...printerSettings, labelShowProblem: e.target.checked})}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white/70">Panne</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={printerSettings.labelShowPrice !== false}
                            onChange={(e) => setPrinterSettings({...printerSettings, labelShowPrice: e.target.checked})}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white/70">Prix</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={printerSettings.labelShowPin !== false}
                            onChange={(e) => setPrinterSettings({...printerSettings, labelShowPin: e.target.checked})}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white/70">PIN/Code</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={printerSettings.labelShowClient !== false}
                            onChange={(e) => setPrinterSettings({...printerSettings, labelShowClient: e.target.checked})}
                            className="w-4 h-4 rounded accent-purple-500"
                          />
                          <span className="text-white/70">Nom client</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bouton Sauvegarder */}
              <div className="flex justify-end">
                <Button
                  onClick={savePrinterSettings}
                  className="bg-green-600 hover:bg-green-700 text-white px-6"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les imprimantes
                </Button>
              </div>

              {/* Liste des imprimantes disponibles */}
              {availablePrinters.length > 0 && (
                <div className="bg-gray-800/50 rounded-xl border border-white/10 p-6">
                  <h3 className="text-lg font-semibold text-white/80 mb-4 flex items-center gap-2">
                    <Printer className="w-5 h-5" />
                    Imprimantes détectées ({availablePrinters.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {availablePrinters.map((printer, idx) => (
                      <div 
                        key={idx} 
                        className={`px-3 py-2 rounded-lg text-sm ${
                          printer === printerSettings.posPrinterName || printer === printerSettings.labelPrinterName
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : 'bg-white/5 text-white/60'
                        }`}
                      >
                        {printer}
                        {printer === printerSettings.posPrinterName && ' (POS)'}
                        {printer === printerSettings.labelPrinterName && ' (Étiquettes)'}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Onglet Étiquettes - Configuration complète */}
          {activeTab === 'labels' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Tag className="w-6 h-6 text-amber-400" />
                  Configuration des étiquettes
                </h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Colonne gauche - Paramètres */}
                <div className="space-y-4">
                  {/* Mode automatique */}
                  <div className="bg-gray-800/50 rounded-xl border border-amber-500/30 p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={labelSettings.autoSize !== false}
                        onChange={(e) => setLabelSettings({...labelSettings, autoSize: e.target.checked})}
                        className="w-5 h-5 rounded accent-amber-500"
                      />
                      <div>
                        <span className="text-white font-medium">Ajustement automatique</span>
                        <p className="text-xs text-white/50">Ajuste les tailles selon le contenu (plus de texte = plus petit)</p>
                      </div>
                    </label>
                  </div>

                  {/* Tailles - toujours visibles */}
                  <div className="bg-gray-800/50 rounded-xl border border-white/10 p-4 space-y-3">
                    <h4 className="text-sm font-medium text-white/70">
                      Tailles de police (px) 
                      {labelSettings.autoSize !== false && <span className="text-amber-400 text-xs ml-2">• Ajustées automatiquement</span>}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-white/50">Téléphone (gras)</label>
                        <Input
                          type="number"
                          value={labelSettings.phoneSize}
                          onChange={(e) => setLabelSettings({...labelSettings, phoneSize: parseInt(e.target.value) || 28})}
                          className="bg-white/10 border-white/20 text-white h-8"
                          min="10" max="80"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-white/50">Prix (gras)</label>
                        <Input
                          type="number"
                          value={labelSettings.priceSize}
                          onChange={(e) => setLabelSettings({...labelSettings, priceSize: parseInt(e.target.value) || 24})}
                          className="bg-white/10 border-white/20 text-white h-8"
                          min="10" max="80"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-white/50">Nom client (gras)</label>
                        <Input
                          type="number"
                          value={labelSettings.clientSize}
                          onChange={(e) => setLabelSettings({...labelSettings, clientSize: parseInt(e.target.value) || 16})}
                          className="bg-white/10 border-white/20 text-white h-8"
                          min="8" max="60"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-white/50">N° Ticket</label>
                        <Input
                          type="number"
                          value={labelSettings.ticketSize}
                          onChange={(e) => setLabelSettings({...labelSettings, ticketSize: parseInt(e.target.value) || 20})}
                          className="bg-white/10 border-white/20 text-white h-8"
                          min="8" max="60"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-white/50">Panne</label>
                        <Input
                          type="number"
                          value={labelSettings.problemSize}
                          onChange={(e) => setLabelSettings({...labelSettings, problemSize: parseInt(e.target.value) || 16})}
                          className="bg-white/10 border-white/20 text-white h-8"
                          min="8" max="60"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-white/50">Date</label>
                        <Input
                          type="number"
                          value={labelSettings.dateSize}
                          onChange={(e) => setLabelSettings({...labelSettings, dateSize: parseInt(e.target.value) || 12})}
                          className="bg-white/10 border-white/20 text-white h-8"
                          min="6" max="40"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-white/50">PIN/Code</label>
                        <Input
                          type="number"
                          value={labelSettings.pinSize}
                          onChange={(e) => setLabelSettings({...labelSettings, pinSize: parseInt(e.target.value) || 14})}
                          className="bg-white/10 border-white/20 text-white h-8"
                          min="6" max="40"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Éléments à afficher */}
                  <div className="bg-gray-800/50 rounded-xl border border-white/10 p-4">
                    <h4 className="text-sm font-medium text-white/70 mb-3">Éléments à afficher</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={labelSettings.showTicket !== false}
                          onChange={(e) => setLabelSettings({...labelSettings, showTicket: e.target.checked})}
                          className="w-4 h-4 rounded accent-amber-500"
                        />
                        <span className="text-white/70">N° Ticket</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={labelSettings.showDate !== false}
                          onChange={(e) => setLabelSettings({...labelSettings, showDate: e.target.checked})}
                          className="w-4 h-4 rounded accent-amber-500"
                        />
                        <span className="text-white/70">Date</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={labelSettings.showPhone !== false}
                          onChange={(e) => setLabelSettings({...labelSettings, showPhone: e.target.checked})}
                          className="w-4 h-4 rounded accent-amber-500"
                        />
                        <span className="text-white/70">Téléphone</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={labelSettings.showProblem !== false}
                          onChange={(e) => setLabelSettings({...labelSettings, showProblem: e.target.checked})}
                          className="w-4 h-4 rounded accent-amber-500"
                        />
                        <span className="text-white/70">Panne</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={labelSettings.showPrice !== false}
                          onChange={(e) => setLabelSettings({...labelSettings, showPrice: e.target.checked})}
                          className="w-4 h-4 rounded accent-amber-500"
                        />
                        <span className="text-white/70">Prix</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={labelSettings.showPin !== false}
                          onChange={(e) => setLabelSettings({...labelSettings, showPin: e.target.checked})}
                          className="w-4 h-4 rounded accent-amber-500"
                        />
                        <span className="text-white/70">PIN/Code</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={labelSettings.showClient !== false}
                          onChange={(e) => setLabelSettings({...labelSettings, showClient: e.target.checked})}
                          className="w-4 h-4 rounded accent-amber-500"
                        />
                        <span className="text-white/70">Nom client</span>
                      </label>
                    </div>
                  </div>

                  {/* Bouton Sauvegarder */}
                  <Button
                    onClick={saveLabelSettings}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer les paramètres
                  </Button>
                </div>

                {/* Colonne droite - Aperçu */}
                <div className="space-y-4">
                  <div className="bg-gray-800/50 rounded-xl border border-green-500/30 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Eye className="w-5 h-5 text-green-400" />
                      <h4 className="text-sm font-medium text-green-400">Aperçu en temps réel</h4>
                    </div>
                    <p className="text-xs text-white/50 mb-3">Étiquette 50mm × 30mm</p>
                    
                    {/* Aperçu de l'étiquette */}
                    <div 
                      className="bg-white rounded-lg mx-auto overflow-hidden"
                      style={{ 
                        width: '200px', 
                        height: '120px',
                        padding: '4px'
                      }}
                    >
                      <iframe
                        srcDoc={getLabelPreviewHtml()}
                        title="Aperçu étiquette"
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          transform: 'scale(0.95)',
                          transformOrigin: 'top left'
                        }}
                      />
                    </div>
                    
                    <p className="text-xs text-white/40 mt-3 text-center">
                      Dimensions réelles: 50mm × 30mm
                    </p>
                  </div>

                  {/* Données d'exemple */}
                  <div className="bg-gray-800/50 rounded-xl border border-white/10 p-4">
                    <h4 className="text-sm font-medium text-white/70 mb-3">Données d'exemple (modifiables)</h4>
                    <div className="space-y-2">
                      <Input
                        value={previewReceipt.clientPhone}
                        onChange={(e) => {
                          previewReceipt.clientPhone = e.target.value;
                          setLabelSettings({...labelSettings}); // Force re-render
                        }}
                        placeholder="Téléphone"
                        className="bg-white/10 border-white/20 text-white h-8 text-sm"
                      />
                      <Input
                        value={previewReceipt.clientName}
                        onChange={(e) => {
                          previewReceipt.clientName = e.target.value;
                          setLabelSettings({...labelSettings});
                        }}
                        placeholder="Nom client"
                        className="bg-white/10 border-white/20 text-white h-8 text-sm"
                      />
                      <Input
                        value={previewReceipt.problem}
                        onChange={(e) => {
                          previewReceipt.problem = e.target.value;
                          setLabelSettings({...labelSettings});
                        }}
                        placeholder="Panne"
                        className="bg-white/10 border-white/20 text-white h-8 text-sm"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="number"
                          value={previewReceipt.estimatedPrice}
                          onChange={(e) => {
                            previewReceipt.estimatedPrice = parseInt(e.target.value) || 0;
                            setLabelSettings({...labelSettings});
                          }}
                          placeholder="Prix"
                          className="bg-white/10 border-white/20 text-white h-8 text-sm"
                        />
                        <Input
                          value={previewReceipt.pinCode}
                          onChange={(e) => {
                            previewReceipt.pinCode = e.target.value;
                            setLabelSettings({...labelSettings});
                          }}
                          placeholder="PIN"
                          className="bg-white/10 border-white/20 text-white h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
